"use client";

import {
  type CSSProperties,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import type { DetectionBox, HeroDetectionOverlay } from "@/lib/demo-finetunes";

interface DetectionOverlayProps {
  overlay: HeroDetectionOverlay;
}

const CORRECT_OVERLAY_COLOR = "#22c55e";
const INCORRECT_OVERLAY_COLOR = "#ef4444";

function BoxRect({
  box,
  containerWidth,
  containerHeight,
  color,
  strokeWidth = 2.5,
  opacity,
  className,
  style,
}: {
  box: DetectionBox;
  containerWidth: number;
  containerHeight: number;
  color: string;
  strokeWidth?: number;
  opacity?: number;
  className?: string;
  style?: CSSProperties;
}) {
  const x = box.xMin * containerWidth;
  const y = box.yMin * containerHeight;
  const w = (box.xMax - box.xMin) * containerWidth;
  const h = (box.yMax - box.yMin) * containerHeight;

  return (
    <rect
      x={x}
      y={y}
      width={w}
      height={h}
      fill="none"
      stroke={color}
      strokeWidth={strokeWidth}
      opacity={opacity}
      className={className}
      style={style}
      rx={2}
    />
  );
}

export function DetectionOverlay({ overlay }: DetectionOverlayProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  const measure = useCallback(() => {
    const el = containerRef.current;

    if (!el) {
      return;
    }

    const img = el.parentElement?.querySelector("img");

    if (!img || !img.naturalWidth) {
      return;
    }

    const containerRect = el.parentElement!.getBoundingClientRect();
    const containerW = containerRect.width;
    const containerH = containerRect.height;
    const imgAspect = img.naturalWidth / img.naturalHeight;
    const containerAspect = containerW / containerH;

    let renderW: number;
    let renderH: number;

    if (imgAspect > containerAspect) {
      renderW = containerW;
      renderH = containerW / imgAspect;
    } else {
      renderH = containerH;
      renderW = containerH * imgAspect;
    }

    setSize({ width: renderW, height: renderH });
  }, []);

  useEffect(() => {
    measure();
    window.addEventListener("resize", measure);

    const img = containerRef.current?.parentElement?.querySelector("img");

    if (img) {
      img.addEventListener("load", measure);
    }

    return () => {
      window.removeEventListener("resize", measure);

      if (img) {
        img.removeEventListener("load", measure);
      }
    };
  }, [measure]);

  if (!size.width || !size.height) {
    return <div ref={containerRef} className="absolute inset-0" />;
  }

  return (
    <div
      ref={containerRef}
      className="pointer-events-none absolute inset-0 flex items-center justify-center"
    >
      <svg
        width={size.width}
        height={size.height}
        viewBox={`0 0 ${size.width} ${size.height}`}
        className="pointer-events-none"
      >
        {overlay.predictions.map((box, index) => {
          const isCorrect = box.correct !== false;
          const color = isCorrect
            ? CORRECT_OVERLAY_COLOR
            : INCORRECT_OVERLAY_COLOR;
          const pulseStyle = isCorrect
            ? { animationDelay: `${(index % 4) * 140}ms` }
            : undefined;

          return isCorrect ? (
            <g key={`pred-${index}`}>
              <BoxRect
                box={box}
                containerWidth={size.width}
                containerHeight={size.height}
                color={color}
                strokeWidth={8}
                opacity={0.1}
                className="ft-overlay-pulse"
                style={pulseStyle}
              />
              <BoxRect
                box={box}
                containerWidth={size.width}
                containerHeight={size.height}
                color={color}
                strokeWidth={4.5}
                opacity={0.24}
              />
              <BoxRect
                box={box}
                containerWidth={size.width}
                containerHeight={size.height}
                color={color}
                strokeWidth={2.5}
              />
            </g>
          ) : (
            <BoxRect
              key={`pred-${index}`}
              box={box}
              containerWidth={size.width}
              containerHeight={size.height}
              color={color}
              strokeWidth={2.5}
            />
          );
        })}
      </svg>
    </div>
  );
}
